import { useEffect, useMemo, useRef, useState } from "react";
import { createOrderWriter } from "@/lib/order-writes";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Box, Check, ChevronRight, Crosshair, Flag, Focus as FocusIcon, History, Plus, Rows3, X } from "lucide-react";
import type { TaskWithFlags, TodayView } from "@desk/contracts";
import type { TaskGroup, TodayNowModel, TodayPullNextModel } from "@/concepts/types";
import { Button } from "@/components/ui/button";
import { FOCUS_MODE_KEY } from "@/features/today/focus-mode";
import { Segmented, Tooltip } from "@/components/ui/misc";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PriorityIcon, ProjectGlyph } from "@/components/icons";
import { locationLabel, useHierarchy, useTaskList, useToday, type Hierarchy } from "@/data/hooks";
import { useApi } from "@/data/context";
import { useTaskActions } from "@/data/actions";
import { describeError } from "@/data/api";
import { qk } from "@/data/cache";
import { daysBetween, formatDay } from "@/lib/dates";
import { sortPlanned, sortTasks } from "@/lib/filters";
import { useStored } from "@/lib/storage";
import { cn, plural } from "@/lib/utils";
import { useConcept } from "@/features/concept-provider";
import { TaskListView, taskRegistry } from "@/features/list/TaskListView";
import { openTask, ui, useUi } from "@/features/ui-state";
import { TaskMenu } from "@/features/task/TaskMenu";
import { TaskSnoozeMenu } from "@/features/today/SnoozeMenu";
import { applySkips, byIds, rankPullNext, skipDown, type SkipState } from "@/features/today/now-order";
import { useOverlay } from "@/data/outbox";
import { useCurrentDestination } from "./ContainerScreens";
import { ScreenError } from "./collection";
import { SpaceIcon } from "@/components/container-icon";
import { mergeSubsetOrder } from "@/lib/ordering";

interface Focus {
  kind: "area" | "space" | "project";
  id: string;
  date: string;
}

function inFocus(t: TaskWithFlags, f: Focus | null) {
  if (!f) return true;
  return f.kind === "area" ? t.areaId === f.id : f.kind === "space" ? t.spaceId === f.id : t.projectId === f.id;
}

function focusName(f: Focus | null, h: Hierarchy) {
  if (!f) return null;
  return f.kind === "area" ? h.area(f.id)?.name : f.kind === "space" ? h.space(f.id)?.name : h.project(f.id)?.name;
}

function FocusChip({ focus, onChange }: { focus: Focus | null; onChange: (f: Focus | null) => void }) {
  const h = useHierarchy();
  const [open, setOpen] = useState(false);
  const name = focusName(focus, h);
  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[13px] transition-colors",
              focus ? "border-foreground/25 bg-card font-medium text-foreground" : "border-dashed border-border text-muted-foreground hover:bg-hover hover:text-foreground",
            )}
          >
            <Crosshair className="size-3.5" />
            {name ? `Focus: ${name}` : "Focus"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command loop>
            <CommandInput placeholder="Focus on…" autoFocus />
            <CommandList>
              <CommandEmpty>No match.</CommandEmpty>
              {h.areas.map((a) => (
                <CommandGroup key={a.id} heading={a.name}>
                  <CommandItem value={`area ${a.name} ${a.id}`} onSelect={() => (onChange({ kind: "area", id: a.id, date: h.today }), setOpen(false))}>
                    <Box />
                    {a.name}
                  </CommandItem>
                  {h.spacesOf(a.id).map((s) => [
                    <CommandItem key={s.id} value={`space ${s.name} ${s.id}`} onSelect={() => (onChange({ kind: "space", id: s.id, date: h.today }), setOpen(false))}>
                      <SpaceIcon space={s} className="size-4 text-[9px]" />
                      {s.name}
                    </CommandItem>,
                    ...h
                      .projectsOf(s.id)
                      .filter((p) => p.state !== "completed")
                      .map((p) => (
                        <CommandItem key={p.id} value={`project ${p.name} ${s.name} ${p.id}`} onSelect={() => (onChange({ kind: "project", id: p.id, date: h.today }), setOpen(false))}>
                          <ProjectGlyph state={p.state} className="ml-1" />
                          {p.name}
                          <span className="text-[12px] text-subtle">{s.name}</span>
                        </CommandItem>
                      )),
                  ])}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {focus && (
        <Tooltip content="Clear focus">
          <Button size="icon-sm" variant="quiet" aria-label="Clear focus" onClick={() => onChange(null)}>
            <X />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

function AddTasksDialog({ open, onOpenChange, focus, plannedIds }: { open: boolean; onOpenChange: (o: boolean) => void; focus: Focus | null; plannedIds: Set<string> }) {
  const h = useHierarchy();
  const a = useTaskActions();
  const [selected, setSelected] = useState<string[]>([]);
  const [onlyFocus, setOnlyFocus] = useState(true);
  const query = useMemo(() => {
    const f = onlyFocus ? focus : null;
    return {
      sort: "priority" as const,
      limit: 500,
      ...(f?.kind === "area" ? { areaId: f.id } : f?.kind === "space" ? { spaceId: f.id } : f?.kind === "project" ? { projectId: f.id } : {}),
    };
  }, [focus, onlyFocus]);
  const { tasks, isLoading } = useTaskList(query, undefined, open);
  const candidates = useMemo(
    () => sortTasks(tasks.filter((t) => !plannedIds.has(t.id) && !t.flags.isSnoozed && t.status !== "done" && t.status !== "cancelled"), "priority"),
    [tasks, plannedIds],
  );
  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const add = async () => {
    if (!selected.length) return;
    onOpenChange(false);
    const r = await a.planAdd(selected);
    if (r.status !== "failed") toast(`Added ${plural(selected.length, "task")} to Today`);
  };
  const fName = focusName(focus, h);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent position="top" hideClose className="max-w-[560px] overflow-hidden p-0">
        <DialogTitle className="sr-only">Add tasks to Today</DialogTitle>
        <Command loop>
          <CommandInput placeholder="Search tasks to add…" autoFocus />
          {fName && (
            <div className="flex items-center gap-2 border-b border-border px-3 py-1.5">
              <Segmented
                ariaLabel="Candidate scope"
                size="sm"
                value={onlyFocus ? "focus" : "all"}
                onChange={(v) => setOnlyFocus(v === "focus")}
                options={[
                  { value: "focus", label: fName },
                  { value: "all", label: "Everywhere" },
                ]}
              />
            </div>
          )}
          <CommandList className="max-h-[52vh]">
            <CommandEmpty>{isLoading ? "Loading…" : "No open tasks to add."}</CommandEmpty>
            <CommandGroup>
              {candidates.map((t) => {
                const on = selected.includes(t.id);
                return (
                  <CommandItem key={t.id} value={`${t.title} ${t.readableId} ${t.id}`} onSelect={() => toggle(t.id)} aria-selected={on}>
                    <span
                      className={cn(
                        "inline-flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]",
                        on ? "border-foreground bg-foreground text-background" : "border-foreground/30",
                      )}
                    >
                      {on && <Check className="!size-2.5 !text-background" strokeWidth={3.5} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{t.title}</span>
                    {t.deadline && <span className="text-[12px] text-subtle">{formatDay(t.deadline, h.today)}</span>}
                    {t.priority !== "none" && <PriorityIcon priority={t.priority} className="!size-3.5" />}
                    <span className="w-14 text-right text-[12px] tabular text-subtle">{t.readableId}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        <DialogFooter>
          <span className="mr-auto text-[12.5px] text-muted-foreground">{selected.length ? `${selected.length} selected` : null}</span>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="default" disabled={!selected.length} onClick={() => void add()}>
            Add to Today
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CarryoverStrip({ rows, date }: { rows: TodayView["carryover"]; date: string }) {
  const api = useApi();
  const qc = useQueryClient();
  const h = useHierarchy();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const act = async (taskId: string, action: "keep" | "reschedule" | "remove", toDate?: string) => {
    try {
      await api.carryover(date, taskId, action, toDate);
    } catch (e) {
      toast.error("Couldn't update the plan", { description: describeError(e) });
    }
    await qc.invalidateQueries({ queryKey: ["today"] });
  };
  const keepAll = async () => {
    setBusy(true);
    for (const r of rows) await act(r.task.id, "keep");
    setBusy(false);
  };
  const yesterday = rows.every((r) => r.carriedFrom && daysBetween(r.carriedFrom, h.today) === 1);
  return (
    <section aria-label="Carried over">
      <div className="flex h-8 items-center gap-1.5 px-2.5 text-[13px] text-muted-foreground">
        <History className="size-3.5 text-subtle" aria-hidden />
        <span>
          {rows.length} from {yesterday ? "yesterday" : "earlier"}
        </span>
        <span aria-hidden className="text-subtle">·</span>
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="rounded px-1 font-medium text-foreground/80 hover:bg-hover hover:text-foreground">
          {open ? "Done" : "Review"}
        </button>
        {!open && (
          <>
            <span aria-hidden className="text-subtle">·</span>
            <button type="button" disabled={busy} onClick={() => void keepAll()} className="rounded px-1 hover:bg-hover hover:text-foreground">
              Keep all
            </button>
          </>
        )}
      </div>
      {open && (
        <ul className="mb-2 mt-0.5 flex flex-col">
          {rows.map((r) => (
            <li key={r.task.id} className="group/co flex min-h-9 items-center gap-2 rounded-lg pl-7 pr-1 hover:bg-hover">
              <button type="button" className="min-w-0 flex-1 truncate text-left text-[14.5px]" onClick={() => openTask(r.task.id, "overlay")}>
                {r.task.title}
              </button>
              {r.carriedFrom && <span className="shrink-0 text-[12px] text-subtle">{formatDay(r.carriedFrom, h.today)}</span>}
              <div className="flex shrink-0 items-center">
                <Button size="xs" variant="ghost" onClick={() => void act(r.task.id, "keep")}>
                  Keep
                </Button>
                <DatePicker value={null} today={h.today} onChange={(d) => d && void act(r.task.id, "reschedule", d)} quick={["tomorrow", "nextWeek"]} align="end">
                  <Button size="xs" variant="ghost">
                    Reschedule
                  </Button>
                </DatePicker>
                <Button size="xs" variant="ghost" onClick={() => void act(r.task.id, "remove")}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function TodayScreen() {
  const { concept } = useConcept();
  const h = useHierarchy();
  const api = useApi();
  const qc = useQueryClient();
  const a = useTaskActions();
  const date = h.today;
  useCurrentDestination(null);
  const { view, error, refetch } = useToday(date, h.timezone);
  const [storedFocus, setFocus] = useStored<Focus | null>("todayFocus", null);
  const focus = storedFocus && storedFocus.date === date ? storedFocus : null;
  const [addOpen, setAddOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useStored("todayCompletedOpen", false);
  const [outsideOpen, setOutsideOpen] = useState(false);
  const [groupBySpace, setGroupBySpace] = useStored("today.groupBySpace", false);
  const [focusMode, setFocusMode] = useStored(FOCUS_MODE_KEY, false);
  const [manualOrder, setManualOrder] = useState<string[] | null>(null);
  const manualPending = useRef(false);
  const writeOrder = useMemo(() => createOrderWriter(api, `plan:${date}`), [api, date]);
  useEffect(() => { if (!manualPending.current) setManualOrder(null); }, [view?.planned]);

  const mode = view?.mode ?? "priority";
  const setMode = async (m: "priority" | "manual") => {
    qc.setQueryData<TodayView>(qk.today(date, h.timezone), (v) => (v ? { ...v, mode: m } : v));
    try {
      await api.planMode(date, m);
    } catch (e) {
      toast.error("Couldn't change the order", { description: describeError(e) });
    }
    void qc.invalidateQueries({ queryKey: ["today"] });
  };

  const plannedAll = useMemo(() => {
    let rows = sortPlanned(view?.planned ?? [], mode);
    if (manualOrder && mode === "manual") {
      const idx = new Map(manualOrder.map((id, i) => [id, i]));
      rows = [...rows].sort((x, y) => (idx.get(x.task.id) ?? 1e9) - (idx.get(y.task.id) ?? 1e9));
    }
    return rows;
  }, [view?.planned, mode, manualOrder]);
  // Skip ("move it down one") is a per-device, per-day reorder on top of the priority/manual order.
  const [skip, setSkip] = useStored<SkipState | null>("today.skip", null);
  const plannedBase = useMemo(() => plannedAll.filter((r) => inFocus(r.task, focus)).map((r) => r.task), [plannedAll, focus]);
  const planned = useMemo(() => byIds(plannedBase, applySkips(plannedBase.map((t) => t.id), skip, date)), [plannedBase, skip, date]);
  const plannedIds = useMemo(() => new Set([...(view?.planned ?? []), ...(view?.carryover ?? [])].map((r) => r.task.id)), [view]);
  const due = (view?.due ?? []).map((r) => r.task).filter((t) => !plannedIds.has(t.id));
  const dueIn = due.filter((t) => inFocus(t, focus));
  const dueOut = due.filter((t) => !inFocus(t, focus));
  const completed = (view?.completed ?? []).map((r) => r.task);
  const progress = { done: completed.length, total: plannedAll.length + completed.length };

  const fName = focusName(focus, h);
  const groupPlanned = (tasks: TaskWithFlags[]): TaskGroup[] => {
    if (!groupBySpace || mode === "manual") return [{ key: "planned", label: null, tasks }];
    const order: string[] = [];
    const buckets = new Map<string, TaskWithFlags[]>();
    for (const t of tasks) {
      const k = t.spaceId ?? t.areaId ?? "inbox";
      if (!buckets.has(k)) (buckets.set(k, []), order.push(k));
      buckets.get(k)!.push(t);
    }
    return order.map((k) => ({
      key: k,
      label: h.space(k)?.name ?? h.area(k)?.name ?? "Inbox",
      tasks: buckets.get(k)!,
    }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const plannedGroups = useMemo(() => groupPlanned(planned), [planned, groupBySpace, mode, h]);
  // "Up next": the plan without its first task, for concepts that feature that task as "Now".
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const upNextGroups = useMemo(() => groupPlanned(planned.slice(1)), [planned, groupBySpace, mode, h]);
  const nowTask = planned[0] ?? null;

  /** Apply a reorder of the visible rows to the whole plan; rows hidden by Focus keep their slots. */
  const savePlanOrder = (visible: string[]) => {
    const previous = plannedAll.map((r) => r.task.id);
    const full = mergeSubsetOrder(previous, visible);
    void writeOrder(full, previous, {
      apply: (next, pending) => { manualPending.current = pending; setManualOrder(next); },
      write: (next) => api.planReorder(date, next),
      onError: (error) => toast.error("Couldn't save the order", { description: describeError(error) }),
      onSettled: () => void qc.invalidateQueries({ queryKey: ["today"] }),
    });
  };
  const plannedListProps = {
    hideTodayStar: true,
    location: (t: TaskWithFlags) => (groupBySpace && mode !== "manual" ? (h.project(t.projectId)?.name ?? null) : locationLabel(t, h)),
    carriedFrom: (t: TaskWithFlags) => view?.planned.find((r) => r.task.id === t.id)?.carriedFrom ?? null,
  };
  const plannedList = (
    <TaskListView
      {...plannedListProps}
      groups={plannedGroups}
      ariaLabel="Planned for today"
      onReorder={mode === "manual" ? savePlanOrder : undefined}
      empty={
        // Until the plan loads (or when it failed), stay blank rather than claim nothing is planned.
        !view ? (
          <div className="h-24" aria-busy={!error} />
        ) : (
        <concept.EmptyState
          message={fName ? `Nothing planned for ${fName} today.` : "Nothing planned for today."}
          action={
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
              <Plus />
              Add tasks
            </Button>
          }
        />
        )
      }
    />
  );
  const upNextList = nowTask ? (
    <TaskListView
      {...plannedListProps}
      groups={upNextGroups}
      ariaLabel="Up next"
      onReorder={mode === "manual" ? savePlanOrder : undefined}
    />
  ) : null;

  // The featured "Now" task: registered for keyboard shortcuts like any rendered row.
  const overlay = useOverlay();
  const selectedId = useUi((s) => s.selectedTaskId);
  useEffect(() => {
    if (!nowTask) return;
    taskRegistry.set(nowTask.id, nowTask);
    return () => {
      if (taskRegistry.get(nowTask.id) === nowTask) taskRegistry.delete(nowTask.id);
    };
  }, [nowTask]);
  const now: TodayNowModel | null = nowTask
    ? {
        task: nowTask,
        location: locationLabel(nowTask, h),
        sync: overlay.marks.get(nowTask.id) ?? null,
        selected: selectedId === nowTask.id,
        menu: <TaskMenu task={nowTask} />,
        rowProps: {
          id: `task-${nowTask.id}`,
          "data-task-id": nowTask.id,
          tabIndex: -1,
          onKeyDown: (e) => {
            if (e.key === "Enter" && e.target === e.currentTarget) openTask(nowTask.id);
          },
        },
        onSelect: () => ui.set({ selectedTaskId: nowTask.id }),
        onPause: () => void a.setStatus(nowTask, "todo"),
        canSkip: planned.length > 1,
        onSkip: () => setSkip(skipDown(plannedBase.map((t) => t.id), skip, date, nowTask.id)),
        onToggleChecklistItem: (itemId, done) => void a.checklistUpdate(nowTask, itemId, { done }),
        snoozeMenu: (trigger) => <TaskSnoozeMenu task={nowTask}>{trigger}</TaskSnoozeMenu>,
      }
    : null;

  // "Pull next": best open task from the focus (or everywhere) that isn't on today's plan yet.
  const candidateQuery = useMemo(
    () => ({
      sort: "priority" as const,
      limit: 500,
      ...(focus?.kind === "area" ? { areaId: focus.id } : focus?.kind === "space" ? { spaceId: focus.id } : focus?.kind === "project" ? { projectId: focus.id } : {}),
    }),
    [focus],
  );
  const candidateList = useTaskList(candidateQuery);
  const candidates = useMemo(
    () =>
      rankPullNext(
        candidateList.tasks.filter((t) => inFocus(t, focus)),
        plannedIds,
      )
        .slice(0, 20)
        .map((task) => ({ task, location: locationLabel(task, h) })),
    [candidateList.tasks, focus, plannedIds, h],
  );
  const pullNext: TodayPullNextModel = {
    candidates,
    loading: candidateList.isLoading,
    onAdd: (t) =>
      void a.planAdd([t.id]).then((r) => {
        if (r.status !== "failed") toast(`Added ${t.readableId === "DSK-0" ? "task" : t.readableId} to Today`);
      }),
  };

  const dueList = (
    <TaskListView
      ariaLabel="Due and overdue"
      hideTodayStar
      groups={[{ key: "due", label: "Due", icon: <Flag className="size-3.5 text-subtle" />, tasks: dueIn }]}
    />
  );

  const dueOutside =
    dueOut.length > 0 ? (
      <div>
        <button
          type="button"
          onClick={() => setOutsideOpen(!outsideOpen)}
          aria-expanded={outsideOpen}
          className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] text-muted-foreground hover:bg-hover hover:text-foreground"
        >
          <ChevronRight className={cn("size-3.5 transition-transform", outsideOpen && "rotate-90")} />
          {plural(dueOut.length, "task")} due outside {fName}
        </button>
        {outsideOpen && <TaskListView ariaLabel="Due outside focus" groups={[{ key: "due-out", label: null, tasks: dueOut }]} />}
      </div>
    ) : null;

  const completedList =
    completed.length > 0 ? (
      <div>
        <button
          type="button"
          onClick={() => setCompletedOpen(!completedOpen)}
          aria-expanded={completedOpen}
          className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] text-muted-foreground hover:bg-hover hover:text-foreground"
        >
          {completedOpen ? "Hide completed" : `Show ${completed.length} completed`}
        </button>
        {completedOpen && <TaskListView ariaLabel="Completed today" hideTodayStar groups={[{ key: "completed", label: null, tasks: completed }]} />}
      </div>
    ) : null;

  return (
    <>
      {!view && <ScreenError error={error} onRetry={() => void refetch()} />}
      <concept.TodayScreen
        date={date}
        timezone={h.timezone}
        mode={mode}
        progress={progress}
        planned={planned}
        due={dueIn}
        dueOutsideFocus={dueOut}
        completed={completed}
        focusLabel={fName ?? null}
        onOpenTask={(t) => openTask(t.id)}
        onComplete={(t) => void a.complete(t)}
        onStart={(t) => void a.setStatus(t, "in_progress")}
        now={now}
        pullNext={pullNext}
        focusMode={focusMode}
        slots={{
          focusModeToggle: (
            <Tooltip content={focusMode ? "Leave focus mode" : "Focus mode"} shortcut="⇧F">
              <Button
                size="icon"
                variant={focusMode ? "secondary" : "quiet"}
                aria-pressed={focusMode}
                aria-label="Focus mode"
                onClick={() => setFocusMode(!focusMode)}
              >
                <FocusIcon />
              </Button>
            </Tooltip>
          ),
          focusChip: <FocusChip focus={focus} onChange={setFocus} />,
          modeToggle: (
            <div className="flex items-center gap-1">
            {mode === "priority" && (
              <Tooltip content={groupBySpace ? "Don't group" : "Group by space"}>
                <Button size="icon" variant={groupBySpace ? "secondary" : "quiet"} aria-pressed={groupBySpace} aria-label="Group by space" onClick={() => setGroupBySpace(!groupBySpace)}>
                  <Rows3 />
                </Button>
              </Tooltip>
            )}
            <Segmented
              ariaLabel="Order"
              size="sm"
              value={mode}
              onChange={(m) => void setMode(m)}
              options={[
                { value: "priority", label: "Priority" },
                { value: "manual", label: "Manual" },
              ]}
            />
            </div>
          ),
          addTasks: (
            <Button size="sm" variant="ghost" onClick={() => setAddOpen(true)}>
              <Plus />
              Add tasks
            </Button>
          ),
          carryover: view && view.carryover.length > 0 ? <CarryoverStrip rows={view.carryover} date={date} /> : null,
          plannedList,
          upNextList,
          dueList,
          dueOutside,
          completedList,
        }}
      />
      <AddTasksDialog open={addOpen} onOpenChange={setAddOpen} focus={focus} plannedIds={plannedIds} />
    </>
  );
}

